import { t } from '../i18n/index.ts'
import { startReceiverPairing } from './deviceLinkService.ts'

export type DeviceLinkReceiverPanelOptions = {
  onComplete: () => void | Promise<void>
  onStatus?: (text: string, kind?: 'muted' | 'ok' | 'err') => void
  /** Bypass QR / rete — torna al form password. */
  onSkip?: () => void
}

/**
 * Monta QR ricevitore. Errori rete → QR offline + Riconnetti + Continua senza accoppiamento.
 */
export async function mountDeviceLinkReceiverPanel(
  root: HTMLElement,
  opts: DeviceLinkReceiverPanelOptions,
): Promise<() => void> {
  const statusEl = root.querySelector('#dl-recv-status') as HTMLElement | null
  const qrWrap = root.querySelector('#dl-recv-qr-wrap') as HTMLElement | null
  const qrImg = root.querySelector('#dl-recv-qr') as HTMLImageElement | null
  const urlEl = root.querySelector('#dl-recv-url') as HTMLElement | null
  const errEl = root.querySelector('#dl-recv-error') as HTMLElement | null
  const retryBtn = root.querySelector('#dl-recv-retry') as HTMLButtonElement | null
  const skipBtn = root.querySelector('#dl-recv-skip') as HTMLButtonElement | null

  const setStatus = (text: string, kind: 'muted' | 'ok' | 'err' = 'muted'): void => {
    if (statusEl) {
      statusEl.textContent = text
      statusEl.className = `status cyber-status ${kind}`
    }
    opts.onStatus?.(text, kind)
  }

  let cancelPairing = (): void => {}
  let cancelled = false

  const showNetworkActions = (msg: string): void => {
    setStatus(msg, 'err')
    if (errEl) {
      errEl.hidden = false
      errEl.textContent = msg
    }
    if (retryBtn) {
      retryBtn.hidden = false
      retryBtn.textContent = '🔄 Riconnetti'
    }
    if (skipBtn) skipBtn.hidden = false
  }

  const start = async (): Promise<void> => {
    if (cancelled) return
    if (retryBtn) retryBtn.hidden = true
    if (skipBtn) skipBtn.hidden = true
    if (errEl) errEl.hidden = true
    setStatus('[INITIALIZING...]')

    try {
      const pairing = await startReceiverPairing((msg) => {
        if (!cancelled) setStatus(msg)
      })
      if (cancelled) {
        pairing.cancel()
        return
      }
      cancelPairing = pairing.cancel

      if (qrImg) qrImg.src = pairing.qrDataUrl
      if (qrWrap) qrWrap.hidden = false
      if (urlEl) urlEl.textContent = pairing.qrText
      root.querySelector('#dl-recv-copy')?.addEventListener('click', () => {
        void navigator.clipboard.writeText(pairing.qrText).catch(() => undefined)
      })

      if (pairing.offline) {
        showNetworkActions(
          'Rete non disponibile. Puoi saltare l’accoppiamento e creare la password subito.',
        )
        // Fallback automatico: form Auth resta usabile (nessun blocco)
        opts.onSkip?.()
        return
      }

      setStatus(t('link_recv_awaiting'))
      if (skipBtn) skipBtn.hidden = false

      void pairing.waitForComplete().then(
        () => {
          if (!cancelled) void opts.onComplete()
        },
        (err) => {
          if (cancelled) return
          const msg = String(err)
          const friendly =
            msg.includes('tunnel_lost') || msg.includes('tunnel_idle')
              ? t('link_recv_tunnel_lost')
              : msg.includes('Failed') || msg.includes('LOAD') || msg.includes('fetch')
                ? 'WebSocket/SSE non raggiungibile. Continua senza accoppiamento o riprova.'
                : msg
          showNetworkActions(friendly)
        },
      )
    } catch (err) {
      if (cancelled) return
      console.error('[ZION] mountDeviceLinkReceiverPanel', err)
      showNetworkActions(err instanceof Error ? err.message : t('link_recv_failed'))
      if (qrWrap && qrWrap.hidden) {
        try {
          const { renderQrDataUrl } = await import('../pairing/qrCode.ts')
          const { buildDeviceLinkUrl } = await import('./deviceLinkProtocol.ts')
          const sid =
            typeof crypto !== 'undefined' && 'randomUUID' in crypto
              ? crypto.randomUUID()
              : `retry-${Date.now()}`
          const text = buildDeviceLinkUrl(sid)
          if (qrImg) qrImg.src = await renderQrDataUrl(text)
          if (urlEl) urlEl.textContent = text
          qrWrap.hidden = false
        } catch (qrErr) {
          console.error('[ZION] offline QR fallback failed', qrErr)
        }
      }
    }
  }

  retryBtn?.addEventListener('click', () => {
    cancelPairing()
    cancelPairing = () => {}
    void start()
  })

  skipBtn?.addEventListener('click', () => {
    cancelled = true
    cancelPairing()
    opts.onSkip?.()
  })

  await start()

  return () => {
    cancelled = true
    cancelPairing()
  }
}

export function deviceLinkReceiverPanelHtml(compact = false): string {
  const hint = compact ? t('link_recv_hint_compact') : t('link_recv_hint_auth')
  return `
    <section class="card center device-link-recv" id="device-link-recv" aria-live="polite">
      <h2 class="section-title">${t('link_recv_title')}</h2>
      <p class="status muted small">${hint}</p>
      <p id="dl-recv-status" class="status muted cyber-status">[INITIALIZING...]</p>
      <div id="dl-recv-qr-wrap" class="qr-wrap" hidden>
        <img id="dl-recv-qr" alt="${t('link_recv_qr_alt')}" width="220" height="220" />
      </div>
      <p class="status muted small" id="dl-recv-url-wrap">
        <span id="dl-recv-url"></span>
        <button type="button" class="secondary small" id="dl-recv-copy">${t('link_recv_copy_link')}</button>
      </p>
      <p id="dl-recv-error" class="status err" hidden></p>
      <button type="button" class="secondary" id="dl-recv-retry" hidden>🔄 Riconnetti</button>
      <button type="button" id="dl-recv-skip" class="big-cta">Salta e Crea Account</button>
    </section>
  `
}

/** QR ricevitore solo su PC / tablet landscape — sul telefono si usa la fotocamera. */
export function shouldShowDeviceLinkQrOnAuth(): boolean {
  const coarse = window.matchMedia('(pointer: coarse)').matches
  const narrow = window.innerWidth < 720
  return !(coarse && narrow)
}
